import React, { useState, useEffect, useRef } from "react";
import {
  ShieldAlert,
  Filter,
  RefreshCw,
  Calendar,
  UserCheck,
  ChevronDown,
  ChevronUp,
  Check,
  BookOpen,
} from "lucide-react";
import api from "../../../../connector/api";

export default function StudentDressCodeTab({ student }) {
  const [records, setRecords] = useState([]);
  const [schoolYears, setSchoolYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState("all");
  const [loading, setLoading] = useState(true);
  const [expandedRows, setExpandedRows] = useState({});

  // Custom Dropdown Ref & State
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Fetch Dress Code Records
  useEffect(() => {
    if (!student?.id) return;

    const fetchInfractions = async () => {
      setLoading(true);
      try {
        const res = await api.get(
          `/students/${student.id}/uniform-infractions/?school_year_id=${selectedYear}`
        );
        setRecords(res.data.records || []);
        if (res.data.school_years) {
          setSchoolYears(res.data.school_years);
        }
      } catch (err) {
        console.error("Failed to load dress code infractions:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchInfractions();
  }, [student?.id, selectedYear]);

  const toggleRow = (id) => {
    setExpandedRows((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const getSelectedYearLabel = () => {
    if (selectedYear === "all") return "All School Years";
    const found = schoolYears.find((sy) => String(sy.id) === String(selectedYear));
    return found ? found.name : "All School Years";
  };

  return (
    <div className="space-y-4 text-xs animate-in fade-in duration-150">
      {/* Top Header & Filter Controls */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-2 border-b border-zinc-100">
        <h3 className="font-bold text-zinc-900 text-xs flex items-center gap-1.5">
          <ShieldAlert className="w-3.5 h-3.5 text-zinc-500" /> Dress Code & Uniform Infractions
        </h3>

        <div className="flex items-center gap-2 self-start sm:self-auto">
          {/* Custom Dropdown Filter */}
          <div className="relative" ref={dropdownRef}>
            <button
              type="button"
              onClick={() => setIsDropdownOpen((prev) => !prev)}
              className="flex items-center gap-2 bg-zinc-50 hover:bg-zinc-100/80 text-zinc-800 px-3 py-1.5 rounded-lg border border-zinc-200/80 transition-all text-xs font-semibold shadow-2xs cursor-pointer focus:outline-none"
            >
              <Filter className="w-3 h-3 text-zinc-400" />
              <span className="text-[11px] font-medium text-zinc-500">School Year:</span>
              <span>{getSelectedYearLabel()}</span>
              <ChevronDown
                className={`w-3.5 h-3.5 text-zinc-400 transition-transform duration-200 ${
                  isDropdownOpen ? "rotate-180" : ""
                }`}
              />
            </button>

            {isDropdownOpen && (
              <div className="absolute right-0 mt-1.5 w-48 bg-white border border-zinc-200/90 rounded-xl shadow-lg z-20 py-1 text-xs animate-in fade-in slide-in-from-top-1 duration-150">
                <button
                  type="button"
                  onClick={() => {
                    setSelectedYear("all");
                    setIsDropdownOpen(false);
                  }}
                  className={`w-full text-left px-3.5 py-2 flex items-center justify-between transition-colors cursor-pointer ${
                    selectedYear === "all"
                      ? "bg-zinc-50 font-bold text-zinc-900"
                      : "text-zinc-600 hover:bg-zinc-50/80 hover:text-zinc-900"
                  }`}
                >
                  <span>All School Years</span>
                  {selectedYear === "all" && <Check className="w-3.5 h-3.5 text-zinc-800" />}
                </button>

                {schoolYears.length > 0 && <div className="my-1 border-t border-zinc-100" />}

                {schoolYears.map((sy) => {
                  const isSelected = String(selectedYear) === String(sy.id);
                  return (
                    <button
                      key={sy.id}
                      type="button"
                      onClick={() => {
                        setSelectedYear(sy.id);
                        setIsDropdownOpen(false);
                      }}
                      className={`w-full text-left px-3.5 py-2 flex items-center justify-between transition-colors cursor-pointer ${
                        isSelected
                          ? "bg-zinc-50 font-bold text-zinc-900"
                          : "text-zinc-600 hover:bg-zinc-50/80 hover:text-zinc-900"
                      }`}
                    >
                      <span>{sy.name}</span>
                      {isSelected && <Check className="w-3.5 h-3.5 text-zinc-800" />}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {loading && (
            <span className="text-zinc-400 flex items-center gap-1 text-[11px]">
              <RefreshCw className="w-3 h-3 animate-spin" /> Syncing...
            </span>
          )}
        </div>
      </div>

      {/* Main Infractions Card List */}
      <div className="border border-zinc-200/80 rounded-lg overflow-hidden bg-white shadow-2xs">
        {loading ? (
          <div className="py-8 text-center text-zinc-400">
            <RefreshCw className="w-4 h-4 animate-spin inline mr-2" />
            Loading uniform infraction records...
          </div>
        ) : records.length === 0 ? (
          <div className="py-8 text-center text-zinc-400">
            No dress code or uniform infractions recorded for this timeframe.
          </div>
        ) : (
          <div className="divide-y divide-zinc-100">
            {records.map((item) => {
              const isExpanded = expandedRows[item.id];
              return (
                <div key={item.id} className="transition-colors hover:bg-zinc-50/50">
                  {/* Summary Header Bar */}
                  <div
                    onClick={() => toggleRow(item.id)}
                    className="p-3.5 flex flex-col sm:flex-row sm:items-center justify-between gap-3 cursor-pointer"
                  >
                    <div className="space-y-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        {/* Cumulative Count Pill */}
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-50 text-amber-700 border border-amber-200/60">
                          Count #{item.cumulative_count || item.total_infractions || 1}
                        </span>

                        {/* Specific Violation Name */}
                        <span className="font-bold text-zinc-900 text-xs">
                          {item.violation || item.specific_item_display || "Dress Code Violation"}
                        </span>

                        {/* School Year & Term Badge */}
                        {item.school_year_name && (
                          <span className="text-[10px] font-medium text-zinc-400 bg-zinc-100 px-1.5 py-0.5 rounded">
                            {item.school_year_name} {item.term_name ? `• ${item.term_name}` : ""}
                          </span>
                        )}
                      </div>

                      {/* Details Sub-row */}
                      <div className="flex items-center gap-3 text-[11px] text-zinc-500 flex-wrap pt-0.5">
                        <span className="flex items-center gap-1 font-mono">
                          <Calendar className="w-3 h-3 text-zinc-400" />
                          {item.date_of_infraction || "—"}
                        </span>

                        <span className="flex items-center gap-1">
                          <BookOpen className="w-3 h-3 text-zinc-400" />
                          {item.subject_name || "General / Homeroom"}
                        </span>

                        <span className="flex items-center gap-1">
                          <UserCheck className="w-3 h-3 text-zinc-400" /> Logged by:{" "}
                          <strong className="text-zinc-700">
                            {item.logged_by_name || "System"}
                          </strong>
                        </span>
                      </div>
                    </div>

                    <div className="flex items-center gap-3 shrink-0 self-end sm:self-auto">
                      {/* Intervention Applied Badge */}
                      {item.intervention_applied_display && (
                        <span className="text-[10px] font-semibold text-zinc-700 bg-zinc-100 px-2 py-0.5 rounded-full border border-zinc-200">
                          {item.intervention_applied_display}
                        </span>
                      )}

                      <button className="text-zinc-400 hover:text-zinc-600 transition-colors cursor-pointer">
                        {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  {/* Expandable Notes Section */}
                  {isExpanded && (
                    <div className="px-3.5 pb-3.5 pt-1 bg-zinc-50/70 border-t border-zinc-100 space-y-2">
                      <div>
                        <span className="text-[10px] font-bold text-zinc-400 uppercase tracking-wider block mb-1">
                          Detailed Remarks & Notes
                        </span>
                        <p className="text-zinc-700 bg-white p-2.5 rounded-lg border border-zinc-200/70 leading-relaxed">
                          {item.detailed_note || "No additional detailed notes recorded for this infraction."}
                        </p>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-[11px] pt-1">
                        <div className="bg-white p-2 rounded-lg border border-zinc-200/70">
                          <span className="text-zinc-400 block text-[10px]">Grade & Section:</span>
                          <span className="font-semibold text-zinc-800">{item.grade_section}</span>
                        </div>

                        <div className="bg-white p-2 rounded-lg border border-zinc-200/70">
                          <span className="text-zinc-400 block text-[10px]">Homeroom Teacher:</span>
                          <span className="font-semibold text-zinc-800">{item.homeroom_teacher}</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}